import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import _, { cloneDeep } from 'lodash';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { LineStateEnum } from 'src/app/samwebDescription/samwin/LineStateEnum';
import { SamwinLineStatusRemoteDto } from 'src/generated/signalR/samwin-line-status-remote-dto';
import { EnabledFeaturesState } from '../../session/enabled-features/enabled-features.state';
import { SamwinCallListSelector } from '../samwin-call-list/samwin-call-list.selector';
import { MessagesLinesChanged } from './samwin-message-lines.actions';

export interface SamwinMessageLinesStateModel {
   sourceLineStatus?: { [callId: number]: SamwinLineStatusRemoteDto };
}

@State<SamwinMessageLinesStateModel>({
   name: 'samwinMessageLines',
   defaults: {
      sourceLineStatus: {},
   },
})
@Injectable()
export class SamwinMessageLinesState {
   @Selector([SamwinMessageLinesState.getSourceLines])
   public static getLineById(lines: SamwinLineStatusRemoteDto[]): (callId: number) => SamwinLineStatusRemoteDto {
      return (callId: number) => lines.find((item) => item.callId === callId);
   }

   @Selector([EnabledFeaturesState.isFeatureEnabled(FeatureEnum.SAMWIN_7_EMAIL)])
   public static isEmailingEnabled(isFeatureEnabled: boolean): boolean {
      return isFeatureEnabled;
   }

   @Selector([SamwinMessageLinesState.isEmailingEnabled, EnabledFeaturesState.isFeatureEnabled(FeatureEnum.PARK_MESSAGE_IN_SKILL)])
   public static canMessageBeParkedAtSkill(isEmailingEnabled: boolean, isFeatureEnabled: boolean): boolean {
      return isEmailingEnabled && isFeatureEnabled;
   }

   @Selector([SamwinMessageLinesState.isEmailingEnabled, EnabledFeaturesState.isFeatureEnabled(FeatureEnum.PARK_MESSAGE_AT_AGENT)])
   public static canMessageBeParkedAtAgent(isEmailingEnabled: boolean, isFeatureEnabled: boolean): boolean {
      return isEmailingEnabled && isFeatureEnabled;
   }

   @Selector([SamwinMessageLinesState])
   public static getSourceLines(state: SamwinMessageLinesStateModel): SamwinLineStatusRemoteDto[] {
      return _(state.sourceLineStatus)
         .map((sls) => sls)
         .value();
   }

   @Selector([SamwinMessageLinesState.getSourceLines])
   public static getSourceLineStates(lineStates: SamwinLineStatusRemoteDto[]): LineStateEnum[] {
      const result: LineStateEnum[] = [];
      lineStates.forEach((sls) => {
         result[sls.callId] = LineStateEnum.valueOf(sls.lineState);
      });
      return result;
   }

   @Selector([SamwinMessageLinesState.getSourceLineStates])
   public static getSourceLineState(lineStates: LineStateEnum[]): (callId: number) => LineStateEnum {
      return (callId: number) => {
         return lineStates[callId] ?? LineStateEnum.IDLE;
      };
   }

   @Selector([SamwinMessageLinesState.getSourceLines])
   public static getIdleLines(lines: SamwinLineStatusRemoteDto[]): SamwinLineStatusRemoteDto[] {
      return lines.filter((line) => {
         const lineState = LineStateEnum.valueOf(line.lineState);
         return !lineState.isRinging && !lineState.isCallInProgress;
      });
   }

   @Selector([SamwinMessageLinesState.getSourceLines])
   public static getAssignedLines(lines: SamwinLineStatusRemoteDto[]): SamwinLineStatusRemoteDto[] {
      return lines.filter((line) => LineStateEnum.valueOf(line.lineState).isRinging);
   }

   @Selector([SamwinMessageLinesState.getSourceLines])
   public static getConnectedLines(lines: SamwinLineStatusRemoteDto[]): SamwinLineStatusRemoteDto[] {
      return lines.filter((line) => LineStateEnum.valueOf(line.lineState).isCallInProgress);
   }

   @Selector([SamwinMessageLinesState.getConnectedLines, SamwinCallListSelector.getMessageIdByCallId])
   public static getConnectedChatLines(
      connectedLines: SamwinLineStatusRemoteDto[],
      getMessageIdByCallId: (callId: number) => string
   ): SamwinLineStatusRemoteDto[] {
      // the call list only contains chat and calls -> entries with a callId and messageId are chats
      return connectedLines.filter((line) => !_.isEmpty(getMessageIdByCallId(line.callId)));
   }

   @Selector([SamwinMessageLinesState.getAssignedLines, SamwinCallListSelector.getMessageIdByCallId])
   public static getAssignedChatLines(idleLines: SamwinLineStatusRemoteDto[], getMessageIdByCallId: (callId: number) => string): SamwinLineStatusRemoteDto[] {
      // the call list only contains chat and calls -> entries with a callId and messageId are chats
      return idleLines.filter((line) => !_.isEmpty(getMessageIdByCallId(line.callId)));
   }

   @Selector([SamwinMessageLinesState.getAssignedLines])
   public static isLineAssigned(lines: SamwinLineStatusRemoteDto[]): (callId: number) => boolean {
      return (callId) => !_.isNil(lines.find((item) => item.callId === callId));
   }

   @Selector([SamwinMessageLinesState.getConnectedLines])
   public static isLineConnected(lines: SamwinLineStatusRemoteDto[]): (callId: number) => boolean {
      return (callId) => !_.isNil(lines.find((item) => item.callId === callId));
   }

   @Selector([SamwinMessageLinesState.getAssignedLines])
   public static isMessageAssigned(assignedLines: SamwinLineStatusRemoteDto[]): boolean {
      return assignedLines?.length > 0 ?? false;
   }

   @Selector([SamwinMessageLinesState.getConnectedLines])
   public static isMessageConnected(connectedLines: SamwinLineStatusRemoteDto[]): boolean {
      return connectedLines?.length > 0 ?? false;
   }

   @Action(MessagesLinesChanged)
   public messagesLinesChanged(ctx: StateContext<SamwinMessageLinesStateModel>, action: MessagesLinesChanged) {
      if (_.isNil(action?.sourceLine?.callId)) {
         return;
      }
      const state = ctx.getState();
      const updatedLines = cloneDeep(state.sourceLineStatus);
      updatedLines[action.sourceLine.callId] = action.sourceLine;
      if (action.sourceLine.lineState === LineStateEnum.IDLE.value) {
         delete updatedLines[action.sourceLine.callId];
      }
      ctx.patchState({
         sourceLineStatus: updatedLines,
      });
   }
}
